import {
  FieldWrapper,
  InputErrorMessage,
  Label,
  NumberInput,
} from '@commenda/kakashi';
import type { PartnershipConfig } from './compute';
import type { OwnershipFieldSettings } from './types';
import { useRevealErrors } from './useRevealErrors';

interface PartnershipSectionsProps {
  config: PartnershipConfig;
  onPatch: (patch: Partial<PartnershipConfig>) => void;
  // ISO-4217 code shown as the money prefix (e.g. "EUR", "USD").
  currency: string;
  // Author-configured, jurisdiction-driven: min capital + helper copy.
  jurisdiction?: { minShareCapital?: number; helperText?: string };
  // True when at least one partner is staged — total capital is then required.
  hasOwners: boolean;
  // Per-field visibility/editability (totalShareCapital).
  fieldSettings: OwnershipFieldSettings;
  required: boolean;
  showErrors: boolean;
}

const Section = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col gap-1">
      <h3 className="text-base font-bold text-text-primary">{title}</h3>
      {description ? (
        <p className="text-sm text-text-secondary">{description}</p>
      ) : null}
    </div>
    {children}
  </div>
);

const MoneyPrefix = (code: string) => () => (
  <span className="flex w-full items-center justify-start txt-small-regular text-text-tertiary">
    {code}
  </span>
);

export const PartnershipSections = ({
  config,
  onPatch,
  currency,
  jurisdiction,
  hasOwners,
  fieldSettings,
  required,
  showErrors,
}: PartnershipSectionsProps) => {
  const capital = fieldSettings.totalShareCapital;
  const minCapital = jurisdiction?.minShareCapital;
  // Reveals its error on blur; a submit/next attempt reveals it too.
  const { touch, reveal } = useRevealErrors(showErrors);
  const belowMinCapital =
    minCapital != null &&
    config.totalShareCapital != null &&
    config.totalShareCapital < minCapital &&
    reveal('totalShareCapital');
  const capitalMissing =
    reveal('totalShareCapital') &&
    (required || hasOwners) &&
    !(config.totalShareCapital && config.totalShareCapital >= 1);

  return (
    <div className="flex flex-col gap-8">
      <Section title="Partnership Capital">
        {capital.visible ? (
          <FieldWrapper>
            <Label
              required={required}
              className="!font-normal text-text-tertiary"
            >
              Total initial capital contribution
            </Label>
            <NumberInput
              formatWithCommas
              className="pl-12"
              placeholder="500,000"
              value={config.totalShareCapital ?? undefined}
              leftEl={MoneyPrefix(currency)}
              error={belowMinCapital || capitalMissing ? ' ' : undefined}
              showErrorMessage={false}
              disabled={!capital.editable}
              onBlur={() => touch('totalShareCapital')}
              onChange={(n) => onPatch({ totalShareCapital: n ?? undefined })}
            />
            {belowMinCapital ? (
              <InputErrorMessage
                message={`Minimum share capital is ${currency} ${(minCapital as number).toLocaleString('en-US')}.`}
              />
            ) : capitalMissing ? (
              <InputErrorMessage message="Total capital contribution is required to save partners." />
            ) : (
              <span className="txt-extra-small-regular text-text-tertiary">
                {jurisdiction?.helperText ??
                  'Sum of partner capital contributions must equal this.'}
              </span>
            )}
          </FieldWrapper>
        ) : null}
      </Section>
    </div>
  );
};
